import {
    FileManagementState,
    CloudFile,
    FetchState
} from "@/shared/types";
import S3 from "aws-sdk/clients/s3";
import * as AWS from "aws-sdk/global";
import { CognitoAuth as Auth } from "@/shared/api/auth";
import { Config } from "@/shared/config";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: FileManagementState = {
    uploadedFiles: {},
    downloadedFiles: {}
};

export const useCloudfileStore = defineStore("cloudfile", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        getDownloadedFile () {
            return identifier => {
                if (this.downloadedFiles[identifier] && this.downloadedFiles[identifier].status === FetchState.Loaded) {
                    return this.downloadedFiles[identifier];
                }

                return null;
            };
        },
        getDownloadedLimitExceeded () {
            return identifier => this.downloadedFiles[identifier] &&
                this.downloadedFiles[identifier].exceededLimit === true;
        }
    },
    actions: {
        setUploadedFile (files: { key: string, file: CloudFile }) {
            this.uploadedFiles[files.key] = files.file;
            this.uploadedFiles[files.key].status = FetchState.Loaded;
        },

        setUploadingFile (files: { key: string, file: CloudFile }) {
            this.uploadedFiles[files.key] = files.file;
            this.uploadedFiles[files.key].status = FetchState.Loading;
        },

        setDownloadedFile (state: FileManagementState, files: { key: string, file: CloudFile }) {
            const copy = { ...state.downloadedFiles };
            copy[files.key] = { ...files.file, status: FetchState.Loaded };
            this.downloadedFiles = copy;
        },

        setDownloadingFile (state: FileManagementState, files: { key: string, file: CloudFile }) {
            const copy = { ...state.downloadedFiles };
            copy[files.key] = { ...files.file, status: FetchState.Loading };
            this.downloadedFiles = copy;
        },

        setIntervalCount (state: FileManagementState, files: { key: string, interval: number }) {
            const copy = { ...state.downloadedFiles };
            copy[files.key] = { ...copy[files.key], interval: files.interval };
            this.downloadedFiles = copy;
        },

        limitExceeded (key: string) {
            const copy = { ...this.downloadedFiles };
            copy[key] = { ...copy[key], exceededLimit: true };
            this.downloadedFiles = copy;
        },
        upload (args: { key: string, file: CloudFile }): Promise<any> {
            const cloudFiles = { ...args };
            const file = cloudFiles.file;
            this.setUploadingFile(cloudFiles);
            return new Promise<any>((resolve, reject) => {
                Auth.getAwsCredentials()
                    .then(identityid => {
                        file.remoteFileName = file.remoteFileName.replace("$identityid", identityid);
                        const s3 = new S3({
                            apiVersion: "2006-03-01",
                            region: Config.Region
                        });

                        s3.upload({
                                Bucket: file.remoteAddress,
                                Key: file.remoteFileName,
                                Body: file.payload,
                                ACL: "bucket-owner-full-control"
                            })
                            .promise()
                            .then(data => {
                                file.remoteAddress = data.Bucket;
                                file.remoteFileName = data.Key;
                                this.setUploadedFile(cloudFiles);
                                resolve(file);
                            })
                            .catch(err => {
                                reject(err.message);
                            });
                    })
                    .catch(err => {
                        reject(err);
                        return;
                    });
            });
        },

        downloadAndRetry (files: { key: string, file: CloudFile }) {
            const limit = 10;
            let tries = 0;
            this.download({ key: files.key, file: files.file });
            const cancelToken = setInterval(() => {
                this.download({ key: files.key, file: files.file });

                if (tries++ > limit) {
                    clearInterval(cancelToken);
                    this.limitExceeded(files.key);
                }
            }, 30000);
            this.setIntervalCount({ key: files.key, interval: cancelToken });
        },

        download (files: { key: string, file: CloudFile }): Promise<void> {
            const cloudFiles = { ...files };
            const file = cloudFiles.file;
            this.setDownloadingFile(cloudFiles);
            return new Promise<void>((resolve, reject) => {
                Auth.getAwsCredentials()
                    .then(identityid => {
                        file.remoteFileName = file.remoteFileName.replace("$identityid", identityid);
                        const s3 = new S3({
                            apiVersion: "2006-03-01",
                            region: Config.Region
                        });
                        s3.getObject({
                            Bucket: file.remoteAddress,
                            Key: file.remoteFileName
                        }).promise()
                            .then(data => {
                                clearInterval(this.downloadedFiles[files.key].interval);
                                file.payload = JSON.parse(data.Body.toString());
                                this.setDownloadedFile(cloudFiles);
                                resolve();
                            })
                            .catch(err => {
                                if (err.statusCode !== 403) {
                                    const notifierStore = useNotifierStore();
                                    notifierStore.setDanger(
                                        "There was a problem getting your result. Please check back later.");
                                }
                            });
                    })
                    .catch(err => {
                        reject(err);
                        return;
                    });
            });
        }
    }
});
